import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Headset, Send, X, Loader2, UserRound, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREETING = "Hi! I'm the unboxcases support assistant. Ask me anything about deposits, opening cases, battles, selling items, or withdrawals.";

export default function SupportBot({ open, onClose }) {
  const { user } = useAuth();
  const [sessionId] = useState(() => {
    try {
      let s = localStorage.getItem("uc_support_sid");
      if (!s) { s = `sup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("uc_support_sid", s); }
      return s;
    } catch (e) { return `sup-${Date.now()}`; }
  });
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [view, setView] = useState("chat"); // chat | handoff | handoff_done
  const [hEmail, setHEmail] = useState(user?.email || "");
  const [hMsg, setHMsg] = useState("");
  const [hSending, setHSending] = useState(false);
  const [hErr, setHErr] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setHEmail(user?.email || ""); }, [user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 150); }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/support/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I'm having trouble right now. Please try again in a moment, or tap \u201cTalk to a human\u201d above." }]);
    } finally { setSending(false); }
  };

  const submitHandoff = async () => {
    setHErr("");
    const email = hEmail.trim();
    const msg = hMsg.trim();
    if (!email.includes("@")) { setHErr("Please enter a valid email."); return; }
    if (!msg) { setHErr("Please tell us what you need help with."); return; }
    setHSending(true);
    try {
      await axios.post(`${API}/support/ticket`, { session_id: sessionId, email, message: msg, name: user?.name });
      setView("handoff_done");
    } catch (e) {
      setHErr(e?.response?.data?.detail || "Couldn't send — please try again.");
    } finally { setHSending(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  if (!open) return null;

  return createPortal(
    <div data-testid="support-bot" className="fixed bottom-4 right-4 z-[130] w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col rounded-xl overflow-hidden shadow-2xl border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#141021] animate-in slide-in-from-bottom-4 fade-in duration-200">
      {/* header */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 bg-[#15102A] dark:bg-[#151B27] text-white">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] flex items-center justify-center"><Headset className="h-4 w-4" /></span>
          <div className="leading-tight">
            <div className="font-cases uppercase text-sm tracking-tight">Live Support</div>
            <div className="text-[10px] text-white/60 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[#22C55E]" /> Online now</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {view === "chat" && (
            <button data-testid="support-handoff-btn" onClick={() => { setView("handoff"); setHMsg(""); setHErr(""); }}
              className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
              <UserRound className="h-3.5 w-3.5" /> Talk to a human
            </button>
          )}
          <button data-testid="support-close-btn" onClick={onClose} aria-label="Close support"
            className="h-8 w-8 rounded-full flex items-center justify-center text-white/70 hover:bg-white/10 hover:text-white transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {view === "chat" && (
        <>
          {/* messages */}
          <div ref={scrollRef} data-testid="support-messages" className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-[#F6F7FA] dark:bg-[#151B27]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-snug rounded-2xl whitespace-pre-wrap ${m.role === "user"
                  ? "bg-[#15102A] dark:bg-[#8B5CF6] text-white rounded-br-sm"
                  : "bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-white border border-[#2A2440] dark:border-[#2A2440] rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-[#19142A] dark:bg-[#1E1832] border border-[#2A2440] dark:border-[#2A2440] text-[#8A90A0] flex items-center gap-2 text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> typing…
                </div>
              </div>
            )}
          </div>

          {/* composer */}
          <div className="shrink-0 border-t border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#141021] p-2.5 flex items-end gap-2">
            <textarea ref={inputRef} data-testid="support-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
              rows={1} placeholder="Type your question…"
              className="flex-1 resize-none max-h-24 text-sm outline-none bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-white rounded-lg px-3 py-2 border border-transparent focus:border-[#3A2F5C] dark:focus:border-[#3A2F5C]" />
            <button data-testid="support-send-btn" onClick={send} disabled={!input.trim() || sending}
              className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center bg-[#15102A] dark:bg-[#8B5CF6] text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </>
      )}

      {view === "handoff" && (
        <div data-testid="support-handoff-form" className="flex-1 overflow-y-auto p-4 bg-[#F6F7FA] dark:bg-[#151B27]">
          <button onClick={() => setView("chat")} className="flex items-center gap-1.5 text-xs font-semibold text-[#8A90A0] hover:text-white dark:hover:text-white mb-3">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to chat
          </button>
          <div className="font-cases uppercase text-sm text-white dark:text-white mb-1">Message our team</div>
          <p className="text-xs text-[#8A90A0] mb-3">Leave your email and a note — a human agent will follow up. We'll include your chat so far.</p>
          {hErr && <div className="text-xs text-[#DB2777] bg-[#FDE8F1] border border-[#F5C2D9] px-3 py-2 mb-2 rounded">{hErr}</div>}
          <label className="text-[11px] font-bold uppercase text-[#8A90A0]">Your email</label>
          <input data-testid="handoff-email" type="email" value={hEmail} onChange={(e) => setHEmail(e.target.value)} placeholder="you@example.com"
            className="w-full mt-1 mb-3 text-sm outline-none bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-white rounded-lg px-3 py-2 border border-[#2A2440] dark:border-[#2A2440] focus:border-[#3A2F5C]" />
          <label className="text-[11px] font-bold uppercase text-[#8A90A0]">How can we help?</label>
          <textarea data-testid="handoff-message" value={hMsg} onChange={(e) => setHMsg(e.target.value)} rows={5} placeholder="Describe your issue…"
            className="w-full mt-1 mb-3 resize-none text-sm outline-none bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-white rounded-lg px-3 py-2 border border-[#2A2440] dark:border-[#2A2440] focus:border-[#3A2F5C]" />
          <button data-testid="handoff-submit" onClick={submitHandoff} disabled={hSending}
            className="w-full h-10 rounded-lg flex items-center justify-center gap-2 bg-[#15102A] dark:bg-[#8B5CF6] text-white font-semibold hover:opacity-90 disabled:opacity-40 transition-opacity">
            {hSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to a human
          </button>
        </div>
      )}

      {view === "handoff_done" && (
        <div data-testid="support-handoff-done" className="flex-1 flex flex-col items-center justify-center text-center p-6 bg-[#F6F7FA] dark:bg-[#151B27]">
          <CheckCircle2 className="h-12 w-12 text-[#16A34A] mb-3" />
          <div className="font-cases uppercase text-base text-white dark:text-white">Message sent</div>
          <p className="text-sm text-[#8A90A0] mt-1.5 max-w-[260px]">Thanks! Our team will reach out to <span className="font-semibold text-white dark:text-white">{hEmail}</span> as soon as possible.</p>
          <button onClick={() => setView("chat")} className="mt-4 text-sm font-semibold text-[#8B5CF6] hover:underline">Back to chat</button>
        </div>
      )}
    </div>,
    document.body
  );
}

